import axios from 'axios';

const CALENDLY_API_BASE = 'https://api.calendly.com';
const CALENDLY_API_TOKEN = import.meta.env?.VITE_CALENDLY_API_TOKEN;
const CALENDLY_USER_URI = import.meta.env?.VITE_CALENDLY_USER_URI;

// Check if token is a placeholder value
const isPlaceholderToken = (token) => {
  if (!token) return true;
  const placeholders = ['your-calendly-api-token-here', 'your-api-token', 'placeholder'];
  return placeholders?.some(p => token?.toLowerCase()?.includes(p));
};

const calendlyClient = axios?.create({
  baseURL: CALENDLY_API_BASE,
  headers: {
    'Authorization': `Bearer ${CALENDLY_API_TOKEN}`,
    'Content-Type': 'application/json'
  }
});

export const calendlyService = {
  // Get user's event types (consultation types)
  async getEventTypes() {
    try {
      if (!CALENDLY_API_TOKEN || !CALENDLY_USER_URI || isPlaceholderToken(CALENDLY_API_TOKEN)) {
        console.warn('Calendly API credentials not configured');
        return [];
      }

      const response = await calendlyClient?.get('/event_types', {
        params: {
          user: CALENDLY_USER_URI,
          active: true
        }
      });

      return response?.data?.collection?.map(eventType => ({
        id: eventType?.uri,
        name: eventType?.name,
        duration: eventType?.duration,
        description: eventType?.description_plain,
        schedulingUrl: eventType?.scheduling_url,
        active: eventType?.active
      })) || [];
    } catch (error) {
      // Check for authentication error
      if (error?.response?.status === 401) {
        throw new Error('Invalid Calendly API token. Please update VITE_CALENDLY_API_TOKEN with a valid token.');
      }
      console.error('Error fetching Calendly event types:', error?.response?.data || error?.message);
      throw error;
    }
  },

  // Get availability for a specific date range
  async getAvailability(eventTypeUri, startDate, endDate) {
    try {
      if (!CALENDLY_API_TOKEN || isPlaceholderToken(CALENDLY_API_TOKEN)) {
        console.warn('Calendly API token not configured');
        return [];
      }

      const response = await calendlyClient?.get('/event_type_available_times', {
        params: {
          event_type: eventTypeUri,
          start_time: startDate,
          end_time: endDate
        }
      });

      return response?.data?.collection?.map(slot => ({
        startTime: slot?.start_time,
        endTime: slot?.end_time,
        status: slot?.status
      })) || [];
    } catch (error) {
      if (error?.response?.status === 401) {
        throw new Error('Invalid Calendly API token. Please update VITE_CALENDLY_API_TOKEN with a valid token.');
      }
      console.error('Error fetching Calendly availability:', error?.response?.data || error?.message);
      throw error;
    }
  },

  // Get scheduled events for a date range
  async getScheduledEvents(startDate, endDate) {
    try {
      if (!CALENDLY_API_TOKEN || !CALENDLY_USER_URI || isPlaceholderToken(CALENDLY_API_TOKEN)) {
        console.warn('Calendly API credentials not configured');
        return [];
      }

      const response = await calendlyClient?.get('/scheduled_events', {
        params: {
          user: CALENDLY_USER_URI,
          min_start_time: startDate,
          max_start_time: endDate,
          status: 'active'
        }
      });

      return response?.data?.collection?.map(event => ({
        id: event?.uri,
        name: event?.name,
        startTime: event?.start_time,
        endTime: event?.end_time,
        status: event?.status,
        location: event?.location,
        inviteesCounter: event?.invitees_counter
      })) || [];
    } catch (error) {
      if (error?.response?.status === 401) {
        throw new Error('Invalid Calendly API token. Please update VITE_CALENDLY_API_TOKEN with a valid token.');
      }
      console.error('Error fetching Calendly scheduled events:', error?.response?.data || error?.message);
      throw error;
    }
  },

  // Check if Calendly is configured
  isConfigured() {
    return !!(CALENDLY_API_TOKEN && CALENDLY_USER_URI && !isPlaceholderToken(CALENDLY_API_TOKEN));
  }
};
